/**
 * WebSocket Service
 *
 * Real-time connection for live updates.
 */

import { getAccessToken } from '@/stores/auth';
import type { WSMessage } from '@/types';

type MessageHandler = (data: unknown) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private handlers: Map<string, Set<MessageHandler>> = new Map();
  private subscriptions: Set<string> = new Set();
  private pingInterval: number | null = null;
  private isConnecting = false;

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('⏳ WebSocket connection already in progress, skipping...');
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const token = getAccessToken();
    if (!token) {
      console.warn('⚠️ No auth token, cannot connect to WebSocket');
      return;
    }

    this.isConnecting = true;

    // Derive WebSocket URL from API URL (production) or same host (dev)
    const apiUrl = import.meta.env.VITE_API_URL || '';
    let wsUrl: string;
    if (apiUrl) {
      const parsed = new URL(apiUrl);
      const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${wsProtocol}//${parsed.host}/ws?token=${token}`;
    } else {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${token}`;
    }

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('✅ WebSocket connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;

      // Resubscribe to previous channels
      for (const channel of this.subscriptions) {
        this.send({ type: 'subscribe', channel, timestamp: new Date().toISOString() });
      }

      // Start ping interval
      this.startPing();
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WSMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('❌ Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log('🔌 WebSocket closed:', event.code, event.reason);
      this.isConnecting = false;
      this.stopPing();
      this.attemptReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.isConnecting = false;
    };
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.stopPing();
    this.subscriptions.clear();

    if (this.ws) {
      this.ws.close(1000, 'User disconnect');
      this.ws = null;
    }
  }

  /**
   * Subscribe to a channel
   */
  subscribe(channel: string, handler: MessageHandler): () => void {
    // Add to handlers
    if (!this.handlers.has(channel)) {
      this.handlers.set(channel, new Set());
    }
    this.handlers.get(channel)!.add(handler);

    // Subscribe if not already
    if (!this.subscriptions.has(channel)) {
      this.subscriptions.add(channel);
      this.send({ type: 'subscribe', channel, timestamp: new Date().toISOString() });
    }

    // Return unsubscribe function
    return () => {
      const handlers = this.handlers.get(channel);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.handlers.delete(channel);
          this.subscriptions.delete(channel);
          this.send({ type: 'unsubscribe', channel, timestamp: new Date().toISOString() });
        }
      }
    };
  }

  /**
   * Subscribe to operator updates
   */
  subscribeOperators(handler: MessageHandler): () => void {
    return this.subscribe('operators', handler);
  }

  /**
   * Subscribe to specific operator
   */
  subscribeOperator(operatorId: string, handler: MessageHandler): () => void {
    return this.subscribe(`operator:${operatorId}`, handler);
  }

  /**
   * Subscribe to task updates
   */
  subscribeTasks(handler: MessageHandler): () => void {
    return this.subscribe('tasks', handler);
  }

  /**
   * Subscribe to specific task
   */
  subscribeTask(taskId: string, handler: MessageHandler): () => void {
    return this.subscribe(`task:${taskId}`, handler);
  }

  /**
   * Subscribe to escalations
   */
  subscribeEscalations(handler: MessageHandler): () => void {
    return this.subscribe('escalations', handler);
  }

  /**
   * Subscribe to alerts
   */
  subscribeAlerts(handler: MessageHandler): () => void {
    return this.subscribe('alerts', handler);
  }

  /**
   * Subscribe to metrics
   */
  subscribeMetrics(handler: MessageHandler): () => void {
    return this.subscribe('metrics', handler);
  }

  /**
   * Subscribe to all Exchange messages (company-wide)
   */
  subscribeExchange(handler: MessageHandler): () => void {
    return this.subscribe('exchange', handler);
  }

  /**
   * Subscribe to specific Exchange channel (e.g., 'iris', 'operator_123')
   */
  subscribeExchangeChannel(channelId: string, handler: MessageHandler): () => void {
    return this.subscribe(`exchange:${channelId}`, handler);
  }

  /**
   * Handle incoming message
   */
  private handleMessage(message: WSMessage): void {
    const { type, channel, data } = message;

    // Handle pong
    if (type === 'pong') {
      return;
    }

    // Handle errors
    if (type === 'error') {
      console.error('WebSocket error:', data);
      return;
    }

    // Dispatch to channel handlers
    if (channel) {
      const handlers = this.handlers.get(channel);
      if (handlers) {
        for (const handler of handlers) {
          try {
            handler(data);
          } catch (error) {
            console.error('Handler error:', error);
          }
        }
      }
    }

    // Dispatch to type handlers (for global subscriptions)
    const typeHandlers = this.handlers.get(type);
    if (typeHandlers) {
      for (const handler of typeHandlers) {
        try {
          handler(data);
        } catch (error) {
          console.error('Handler error:', error);
        }
      }
    }
  }

  /**
   * Send message to server
   */
  private send(message: WSMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  /**
   * Send custom message (for voice, etc.)
   */
  sendMessage(message: WSMessage): void {
    this.send(message);
  }

  /**
   * Attempt to reconnect
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`🔄 WebSocket reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Start ping interval
   */
  private startPing(): void {
    this.pingInterval = window.setInterval(() => {
      this.send({ type: 'ping', timestamp: new Date().toISOString() });
    }, 30000);
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export const wsService = new WebSocketService();
